import React from 'react';
import { ACTIONS_COL_WIDTH, Alert, ClampText, DataTable, EmptyValue, ServiceAreaPill, Spinner, StatusBadge, type DataTableColumn } from '@/components/ui';
import {
  formatPcrServiceAreaLabel,
  normalizePcrReportTypeBucket,
  formatPcrStatusLabel,
  type PcrStatus,
  type PcrSubmission,
} from '../../../services/pcrService';
import PcrRowActions from './PcrRowActions';
import { formatDateTime } from '@/utils/displayDateTime';

/** Matches table body text: no primary/underline from Bootstrap link styles */
const tablePlainBtnClass =
  'tw-p-0 tw-border-0 tw-bg-transparent tw-text-content tw-no-underline tw-shadow-none tw-align-baseline';
function formatReceived(iso: string): string {
  return formatDateTime(iso) || iso;
}

function formatIso(iso: string | null): string {
  return formatDateTime(iso) || '–';
}

function formatCallback(isFeedback: boolean): string {
  return isFeedback ? 'Yes' : 'No';
}

export interface PcrSubmissionsTableProps {
  submissions: PcrSubmission[];
  loading: boolean;
  error: string | null;
  onDismissError?: () => void;
  pcrReplyUiEnabled?: boolean;
  canPcrReply?: boolean;
  canUpdateStatus?: boolean;
  canMoveToInternalReview?: boolean;
  onMoveToInternalReview?: (row: PcrSubmission) => void;
  canMoveToFinalize?: boolean;
  onMoveToFinalize?: (row: PcrSubmission) => void;
  statusTransitioningId?: string | null;
  onReply?: (row: PcrSubmission) => void;
  onEdit?: (row: PcrSubmission) => void;
  onOpenDetail: (row: PcrSubmission) => void;
  formatRoadLabel?: (row: PcrSubmission) => string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (column: 'createdAt' | 'status' | 'serviceArea' | 'roadName') => void;
  onOpenNotesHistory?: (row: PcrSubmission) => void;
  canOpenNotesHistory?: boolean;
  canPromoteToWorkId?: boolean;
  onPromoteToWorkId?: (row: PcrSubmission) => void;
  canCloseWithoutWork?: boolean;
  onCloseWithoutWork?: (row: PcrSubmission) => void;
  onOpenLinkedWorkId?: (row: PcrSubmission) => void;
  canExportAuditReport?: boolean;
  onExportAuditReport?: (row: PcrSubmission) => void;
  auditReportExportingId?: number | null;
}

const PcrSubmissionsTable: React.FC<PcrSubmissionsTableProps> = ({
  submissions,
  loading,
  error,
  onDismissError,
  canUpdateStatus,
  canMoveToInternalReview,
  onMoveToInternalReview,
  canMoveToFinalize,
  onMoveToFinalize,
  statusTransitioningId,
  pcrReplyUiEnabled,
  canPcrReply,
  onReply,
  onEdit,
  onOpenDetail,
  formatRoadLabel,
  sortBy,
  sortDir,
  onSort,
  onOpenNotesHistory,
  canOpenNotesHistory,
  canPromoteToWorkId,
  onPromoteToWorkId,
  canCloseWithoutWork,
  onCloseWithoutWork,
  onOpenLinkedWorkId,
  canExportAuditReport,
  onExportAuditReport,
  auditReportExportingId,
}) => {
  const hasHistory = Boolean(canOpenNotesHistory && onOpenNotesHistory);
  const showRowActionMenu = Boolean(
    hasHistory ||
      (canExportAuditReport && onExportAuditReport) ||
      (canPcrReply && onReply) ||
      (canUpdateStatus && onEdit) ||
      (canMoveToInternalReview && onMoveToInternalReview) ||
      (canMoveToFinalize && onMoveToFinalize) ||
      (canPromoteToWorkId && onPromoteToWorkId) ||
      (canCloseWithoutWork && onCloseWithoutWork) ||
      (onOpenLinkedWorkId),
  );
  const showTrailingActions = showRowActionMenu;

  if (error) {
    return (
      <Alert variant="danger" className="tw-m-3" dismissible={!!onDismissError} onClose={onDismissError}>
        {error}
      </Alert>
    );
  }

  if (loading && submissions.length === 0) {
    return (
      <div className="tw-flex tw-justify-center tw-p-5">
        <Spinner animation="border" role="status">Loading…</Spinner>
      </div>
    );
  }

  // Mobile card anatomy: PCR ID is the primary identifier (title), Received is the secondary
  // line (subtitle), Status + row actions ride together top-right (trailing) — same corner the
  // desktop actions menu occupies — and the rest fall into the labelled meta grid. Response SLA,
  // Responded and Callback are audit-trail detail that a phone-width triage pass doesn't need;
  // they stay on the desktop table via `mobile: 'hidden'` (dropped from the card only, not the
  // column set).
  const columns: DataTableColumn<PcrSubmission>[] = [
    {
      key: 'pcrId',
      header: 'PCR ID',
      mobile: 'title',
      className: 'tw-whitespace-nowrap',
      cell: (row) => (
        <button
          type="button"
          className={tablePlainBtnClass}
          style={{ fontSize: 'inherit' }}
          onClick={() => onOpenDetail(row)}
          title="Open details"
        >
          {row.report_id || '–'}
        </button>
      ),
    },
    {
      key: 'createdAt',
      header: 'Received',
      sortable: true,
      mobile: 'subtitle',
      className: 'tw-whitespace-nowrap',
      cell: (row) => formatReceived(row.receivedAt),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      mobile: 'trailing',
      cell: (row) => {
        const st = (row.status || 'new') as PcrStatus;
        // The shared status map doesn't have a NEW entry (see report), so this badge falls back
        // to the shared "unknown status" colour; the wording still comes from the PCR-specific
        // labels (New / Internal Review / Finalized) via `label`.
        return <StatusBadge status={st} vocabulary="pcr" label={formatPcrStatusLabel(st)} />;
      },
    },
    {
      key: 'responseSla',
      header: 'Response SLA',
      mobile: 'hidden',
      className: 'tw-text-sm tw-whitespace-nowrap',
      cell: (row) => row.responseTimeBucket || '–',
    },
    {
      key: 'responded',
      header: 'Responded',
      mobile: 'hidden',
      className: 'tw-text-sm tw-whitespace-nowrap',
      cell: (row) => formatIso(row.respondedAt),
    },
    {
      key: 'serviceArea',
      header: 'Service Area',
      sortable: true,
      mobileLabel: 'Service Area',
      cell: (row) => <ServiceAreaPill value={row.service_area_text ? formatPcrServiceAreaLabel(row.service_area_text) : null} />,
    },
    {
      key: 'foremanArea',
      header: 'Foreman Area',
      mobileLabel: 'Foreman Area',
      cell: (row) => (row.foreman_area_name ?? row.community_text) || '–',
    },
    {
      key: 'roadName',
      header: 'Road',
      sortable: true,
      mobileLabel: 'Road',
      cell: (row) => {
        const road = formatRoadLabel ? formatRoadLabel(row) : row.road_text;
        return road && road !== '–' ? <ClampText lines={2}>{road}</ClampText> : <EmptyValue />;
      },
    },
    {
      key: 'reportType',
      header: 'Report Type',
      mobileLabel: 'Report Type',
      cell: (row) => normalizePcrReportTypeBucket(row.report_type),
    },
    {
      key: 'activity',
      header: 'Activity',
      mobileLabel: 'Activity',
      cell: (row) => (row.activity_type ? <ClampText lines={2}>{row.activity_type}</ClampText> : <EmptyValue />),
    },
    {
      key: 'callback',
      header: 'Callback',
      mobile: 'hidden',
      headerClassName: 'tw-whitespace-nowrap',
      className: 'tw-whitespace-nowrap',
      cell: (row) => formatCallback(Boolean(row.is_feedback)),
    },
    {
      key: 'user',
      header: 'User',
      mobileLabel: 'User',
      cell: (row) => row.user_name || '–',
    },
    {
      key: 'message',
      header: 'Message',
      // W-iP4: at 240px the message wrapped to 8 lines and set every row's height. A wide column
      // with a 2-line clamp keeps rows short; the full message is in the tooltip and the detail.
      width: '22rem',
      mobileLabel: 'Message',
      className: 'tw-text-sm',
      cell: (row) =>
        row.message ? (
          <div className="md:tw-min-w-[20rem] md:tw-max-w-[32rem]">
            <ClampText lines={2}>{row.message}</ClampText>
          </div>
        ) : (
          '–'
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: ACTIONS_COL_WIDTH,
      headerClassName: 'tw-whitespace-nowrap',
      className: 'tw-whitespace-nowrap',
      mobile: 'trailing',
      hidden: !showTrailingActions,
      cell: (row) => (
        <PcrRowActions
          row={row}
          pcrReplyUiEnabled={pcrReplyUiEnabled}
          canPcrReply={canPcrReply}
          canUpdateStatus={canUpdateStatus}
          canMoveToInternalReview={canMoveToInternalReview}
          onMoveToInternalReview={onMoveToInternalReview}
          canMoveToFinalize={canMoveToFinalize}
          onMoveToFinalize={onMoveToFinalize}
          statusTransitioningId={statusTransitioningId}
          onReply={onReply}
          onEdit={onEdit}
          onOpenDetail={onOpenDetail}
          canOpenNotesHistory={canOpenNotesHistory}
          onOpenNotesHistory={onOpenNotesHistory}
          canPromoteToWorkId={canPromoteToWorkId}
          onPromoteToWorkId={onPromoteToWorkId}
          canCloseWithoutWork={canCloseWithoutWork}
          onCloseWithoutWork={onCloseWithoutWork}
          onOpenLinkedWorkId={onOpenLinkedWorkId}
          canExportAuditReport={canExportAuditReport}
          onExportAuditReport={onExportAuditReport}
          auditReportExportingId={auditReportExportingId}
        />
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={submissions}
      rowKey={(row) => row.id}
      // The table box fills ListPage.Body and is the one scroll area: sticky header, PCR ID pinned
      // left, Actions pinned right, horizontal scrollbar at the bottom (W-H5, W-iP4).
      fill
      striped
      hover
      sortBy={sortBy}
      sortDir={sortDir}
      onSort={onSort ? (key) => onSort(key as 'createdAt' | 'status' | 'serviceArea' | 'roadName') : undefined}
      emptyMessage="No PCR submissions found."
    />
  );
};

export default PcrSubmissionsTable;
